/**
 * Table maths in the editor (#338).
 *
 * The formula IS the cell's text: a cell whose text starts with `=` is a
 * formula, typed and edited like any other text. Nothing computed is ever
 * written to the document - a decoration hides the formula and shows its
 * value while the cursor is elsewhere, and shows the formula with a small
 * `= value` hint while the cursor is in the cell. So undo, autosave, stale-tab
 * protection and excerpts need nothing new, and the JSON stays plain.
 *
 * The maths lives in `lib/tableMath.ts` (shared with the Markdown export).
 *
 * Totals are ordinary formulas over `ABOVE` / `LEFT` (`=SUM(ABOVE)`), so a row
 * inserted above a total row is counted without rewriting any reference.
 */
import { Extension } from '@tiptap/core';
import { Plugin, PluginKey, TextSelection, type EditorState, type Transaction } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import type { Node as PMNode, ResolvedPos } from '@tiptap/pm/model';
import { TableMap } from '@tiptap/pm/tables';
import {
  evaluateTable,
  formatResult,
  isFormula,
  totalFormula,
  totalFunctionOf,
  type CellResult,
  type TableInput,
  type TotalDirection,
  type TotalFn,
} from '../../lib/tableMath';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    tableMath: {
      /** Append a "Total" row whose cells are `=SUM(ABOVE)`. */
      addTotalRow: (fn?: TotalFn) => ReturnType;
      /** Append a "Total" column whose cells are `=SUM(LEFT)`. */
      addTotalColumn: (fn?: TotalFn) => ReturnType;
      /** Change the function of the total cell at the cursor. */
      setTotalFunction: (fn: TotalFn) => ReturnType;
    };
  }
}

export interface TableMathState {
  /** Result per formula cell, keyed by the cell's document position. */
  results: Map<number, { result: CellResult; formula: string; size: number }>;
  decorations: DecorationSet;
}

export const tableMathPluginKey = new PluginKey<TableMathState>('tableMath');

function isCellNode(node: PMNode): boolean {
  const role = node.type.spec.tableRole;
  return role === 'cell' || role === 'header_cell';
}

/** The table cell containing a resolved position, if any. */
export function findCellAt($pos: ResolvedPos): { node: PMNode; pos: number } | null {
  for (let d = $pos.depth; d > 0; d--) {
    const node = $pos.node(d);
    if (isCellNode(node)) return { node, pos: $pos.before(d) };
  }
  return null;
}

function findTableAt($pos: ResolvedPos): { node: PMNode; pos: number } | null {
  for (let d = $pos.depth; d > 0; d--) {
    const node = $pos.node(d);
    if (node.type.spec.tableRole === 'table') return { node, pos: $pos.before(d) };
  }
  return null;
}

/** The total function of the cell at the cursor, when it holds one. */
export function cellIsTotal(state: EditorState): { fn: TotalFn; direction: TotalDirection } | null {
  const cell = findCellAt(state.selection.$from);
  return cell ? totalFunctionOf(cell.node.textContent) : null;
}

/** Evaluate every table that holds a formula. */
function computeResults(doc: PMNode): TableMathState['results'] {
  const results: TableMathState['results'] = new Map();
  doc.descendants((node, pos) => {
    if (node.type.spec.tableRole !== 'table') return true;
    let hasFormula = false;
    const input: TableInput = [];
    const positions: number[][] = [];
    node.forEach((rowNode, rowOffset) => {
      const cells: TableInput[number] = [];
      const rowPositions: number[] = [];
      rowNode.forEach((cell, cellOffset) => {
        const text = cell.textContent;
        if (!hasFormula && isFormula(text)) hasFormula = true;
        cells.push({
          text,
          header: cell.type.spec.tableRole === 'header_cell',
          colspan: cell.attrs.colspan,
          rowspan: cell.attrs.rowspan,
        });
        // table start + 1 -> row start; row start + 1 -> cell start.
        rowPositions.push(pos + 1 + rowOffset + 1 + cellOffset);
      });
      input.push(cells);
      positions.push(rowPositions);
    });
    if (hasFormula) {
      const evaluated = evaluateTable(input);
      evaluated.forEach((rowResults, r) => {
        rowResults.forEach((result, c) => {
          if (!result) return;
          const cellPos = positions[r][c];
          const cell = doc.nodeAt(cellPos);
          if (!cell) return;
          results.set(cellPos, { result, formula: input[r][c].text.trim(), size: cell.nodeSize });
        });
      });
    }
    // Tables never nest inside a cell in our schema, but descending is
    // harmless and keeps this right if that ever changes.
    return true;
  });
  return results;
}

function valueWidget(text: string, className: string): HTMLElement {
  const span = document.createElement('span');
  span.className = className;
  span.contentEditable = 'false';
  // textContent, never innerHTML: formulas and results are user text.
  span.textContent = text;
  return span;
}

function buildDecorations(state: EditorState, results: TableMathState['results']): DecorationSet {
  if (results.size === 0) return DecorationSet.empty;
  const { $from, $to } = state.selection;
  const decorations: Decoration[] = [];
  for (const [pos, { result, formula, size }] of results) {
    const end = pos + size;
    const editing = $from.pos > pos && $from.pos < end && $to.pos > pos && $to.pos < end;
    const shown = formatResult(result);
    const classes = ['table-formula-cell'];
    if (result.kind === 'error') classes.push('table-formula-error');
    if (editing) classes.push('table-formula-editing');
    decorations.push(Decoration.node(pos, end, { class: classes.join(' '), title: formula }));
    if (editing) {
      decorations.push(
        Decoration.widget(end - 1, () => valueWidget(`= ${shown}`, 'table-formula-preview'), {
          side: 1,
          ignoreSelection: true,
          key: `tfp:${shown}`,
        }),
      );
    } else {
      decorations.push(
        Decoration.widget(pos + 1, () => valueWidget(shown, 'table-formula-value'), {
          side: -1,
          ignoreSelection: true,
          key: `tfv:${shown}`,
        }),
      );
    }
  }
  return DecorationSet.create(state.doc, decorations);
}

function cellText(state: EditorState, text: string): PMNode {
  const paragraph = state.schema.nodes.paragraph;
  return paragraph.create(null, text ? state.schema.text(text) : null);
}

function makeCell(state: EditorState, header: boolean, text: string): PMNode | null {
  const type = header ? state.schema.nodes.tableHeader : state.schema.nodes.tableCell;
  return type ? type.create(null, cellText(state, text)) : null;
}

function addTotalRow(state: EditorState, fn: TotalFn): Transaction | null {
  const table = findTableAt(state.selection.$from);
  if (!table) return null;
  const map = TableMap.get(table.node);
  const cells: PMNode[] = [];
  for (let c = 0; c < map.width; c++) {
    const text = c === 0 && map.width > 1 ? 'Total' : totalFormula(fn, 'ABOVE');
    const cell = makeCell(state, false, text);
    if (!cell) return null;
    cells.push(cell);
  }
  const rowType = state.schema.nodes.tableRow;
  if (!rowType) return null;
  const insertAt = table.pos + table.node.nodeSize - 1;
  return state.tr.insert(insertAt, rowType.create(null, cells));
}

function addTotalColumn(state: EditorState, fn: TotalFn): Transaction | null {
  const table = findTableAt(state.selection.$from);
  if (!table) return null;
  const tr = state.tr;
  const inserts: Array<{ at: number; cell: PMNode }> = [];
  table.node.forEach((rowNode, rowOffset) => {
    let allHeaders = rowNode.childCount > 0;
    rowNode.forEach((cell) => {
      if (cell.type.spec.tableRole !== 'header_cell') allHeaders = false;
    });
    const cell = makeCell(state, allHeaders, allHeaders ? 'Total' : totalFormula(fn, 'LEFT'));
    if (cell) inserts.push({ at: table.pos + 1 + rowOffset + rowNode.nodeSize - 1, cell });
  });
  if (inserts.length === 0) return null;
  // Back to front so earlier positions stay valid.
  for (const { at, cell } of inserts.reverse()) tr.insert(at, cell);
  return tr;
}

function setTotalFunction(state: EditorState, fn: TotalFn): Transaction | null {
  const cell = findCellAt(state.selection.$from);
  if (!cell) return null;
  const current = totalFunctionOf(cell.node.textContent);
  if (!current) return null;
  const from = cell.pos + 1;
  const to = cell.pos + cell.node.nodeSize - 1;
  const formula = totalFormula(fn, current.direction);
  const tr = state.tr.replaceWith(from, to, cellText(state, formula));
  // Keep the cursor at the end of the formula, inside the cell.
  return tr.setSelection(TextSelection.create(tr.doc, from + 1 + formula.length));
}

export const TableMath = Extension.create({
  name: 'tableMath',

  addCommands() {
    return {
      addTotalRow:
        (fn = 'SUM') =>
        ({ state, dispatch }) => {
          const tr = addTotalRow(state, fn);
          if (!tr) return false;
          dispatch?.(tr.scrollIntoView());
          return true;
        },
      addTotalColumn:
        (fn = 'SUM') =>
        ({ state, dispatch }) => {
          const tr = addTotalColumn(state, fn);
          if (!tr) return false;
          dispatch?.(tr.scrollIntoView());
          return true;
        },
      setTotalFunction:
        (fn) =>
        ({ state, dispatch }) => {
          const tr = setTotalFunction(state, fn);
          if (!tr) return false;
          dispatch?.(tr);
          return true;
        },
    };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin<TableMathState>({
        key: tableMathPluginKey,
        state: {
          init: (_config, state) => {
            const results = computeResults(state.doc);
            return { results, decorations: buildDecorations(state, results) };
          },
          apply: (tr, prev, _oldState, newState) => {
            if (!tr.docChanged && !tr.selectionSet) return prev;
            const results = tr.docChanged ? computeResults(newState.doc) : prev.results;
            return { results, decorations: buildDecorations(newState, results) };
          },
        },
        props: {
          decorations: (state) => tableMathPluginKey.getState(state)?.decorations ?? DecorationSet.empty,
          handleDOMEvents: {
            // The value is a non-editable widget; clicking it should edit the
            // formula, so put the cursor at the end of the cell's text.
            mousedown: (view, event) => {
              const target = event.target as HTMLElement | null;
              const valueEl = target?.closest?.('.table-formula-value');
              const cellEl = valueEl?.closest('td, th');
              if (!cellEl || !view.dom.contains(cellEl)) return false;
              let inside: number;
              try {
                inside = view.posAtDOM(cellEl, 0);
              } catch {
                return false;
              }
              const cell = findCellAt(view.state.doc.resolve(inside));
              if (!cell) return false;
              event.preventDefault();
              const end = view.state.doc.resolve(cell.pos + cell.node.nodeSize - 1);
              view.dispatch(view.state.tr.setSelection(TextSelection.near(end, -1)));
              view.focus();
              return true;
            },
          },
        },
      }),
    ];
  },
});
